import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Target, Heart, CheckCircle2, MapPin, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';
import { BreadcrumbSchema } from '../components/SchemaOrg';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const APropos = () => {
  const values = [
    {
      icon: <Award className="w-12 h-12 text-blue-600" />,
      title: 'Excellence Professionnelle',
      description: 'Nous visons l excellence dans chaque intervention avec des plombiers certifiés RGE, formés continuellement aux dernières techniques et normes.'
    },
    {
      icon: <Users className="w-12 h-12 text-blue-600" />,
      title: 'Satisfaction Client',
      description: 'Votre satisfaction est notre priorité absolue. Nous sommes à l écoute de vos besoins et adaptons nos prestations à vos attentes spécifiques.'
    },
    {
      icon: <Target className="w-12 h-12 text-blue-600" />,
      title: 'Réactivité Maximale',
      description: 'Intervention rapide sous 30 minutes pour les urgences. Notre proximité géographique est un atout majeur pour nos clients de Paris 17.'
    },
    {
      icon: <Heart className="w-12 h-12 text-blue-600" />,
      title: 'Transparence Totale',
      description: 'Devis détaillés et transparents, sans frais cachés. Vous savez exactement ce que vous payez avant le début des travaux.'
    }
  ];

  const certifications = [
    'Plombiers certifiés et diplômés (CAP, BP Plomberie)',
    'Assurance décennale et responsabilité civile professionnelle',
    'Respect strict des normes DTU plomberie en vigueur',
    'Matériel professionnel et véhicules équipés',
    'Formation continue de nos équipes techniques',
    'Agréés toutes compagnies d assurances'
  ];

  const breadcrumbItems = [
    { name: 'À propos', url: '/a-propos' }
  ];

  return (
    <>
      <SEO
        title="À Propos - Entreprise Plomberie Paris 17e | NHM"
        description="NHM Plomberie, entreprise locale à Paris 17e. Plombiers certifiés, 10+ ans d'expérience, disponibles 24/7. Excellence et transparence ☎ 09 70 70 58 63"
        keywords="entreprise plomberie Paris 17e, plombier certifié Paris 17, artisan plombier, garantie travaux, assurance décennale plomberie"
        canonical="https://nhm-plomberie.fr/a-propos"
      />
      <BreadcrumbSchema items={[{ name: 'Accueil', url: 'https://nhm-plomberie.fr/' }, ...breadcrumbItems.map(item => ({ name: item.name, url: `https://nhm-plomberie.fr${item.url}` }))]} />

      <Breadcrumb items={breadcrumbItems} />

      <section className="bg-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">À Propos de NHM Plomberie</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Votre entreprise de plomberie de proximité à Paris 17e depuis plus de 10 ans
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Notre Histoire et Notre Expertise</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
              <p>
                <strong>NHM Plomberie</strong> est bien plus qu une simple entreprise de plomberie. C est une histoire de passion 
                pour le métier, de proximité avec nos clients et d engagement quotidien pour la qualité. Fondée il y a plus de 10 ans 
                à <strong>Paris 17e</strong>, notre entreprise s est développée grâce à la confiance que nos clients nous 
                accordent jour après jour.
              </p>
              <p>
                Installés au <strong>18 rue Legendre dans Paris 17 et alentours100</strong>, nous avons choisi cette localisation stratégique 
                pour être au plus près de nos clients des Paris et sa proche banlieue. Cette implantation locale nous permet de garantir des 
                <strong> délais d intervention exceptionnels</strong>, essentiels notamment en cas d urgence plomberie où chaque 
                minute compte pour limiter les dégâts.
              </p>
              <p>
                Notre équipe est composée exclusivement de <strong>plombiers qualifiés et expérimentés</strong>, tous titulaires 
                des diplômes professionnels requis (CAP et BP Plomberie). Nous croyons fermement que l expertise technique doit 
                s accompagner d un véritable sens du service client. C est pourquoi chaque membre de notre équipe est formé non 
                seulement aux aspects techniques de la plomberie, mais également à l écoute et au conseil personnalisé.
              </p>
              <p>
                Au fil des années, nous avons développé une <strong>expertise reconnue</strong> dans tous les domaines de la plomberie : 
                du simple dépannage d urgence aux projets de rénovation complexes, en passant par l installation de systèmes de chauffage 
                modernes et économes en énergie. Notre force réside dans notre polyvalence et notre capacité à intervenir aussi bien 
                chez les particuliers que pour les professionnels, syndics de copropriété et entreprises.
              </p>
              <p>
                Nous nous distinguons par notre <strong>disponibilité permanente</strong> avec un service d astreinte fonctionnel 
                24 heures sur 24, 7 jours sur 7, même les jours fériés. Parce que les urgences n attendent pas, nos plombiers sont 
                toujours prêts à intervenir rapidement avec du matériel professionnel embarqué dans nos véhicules atelier.
              </p>
              <p>
                Chez NHM Plomberie, nous avons à coeur de tisser des <strong>relations durables</strong> avec nos clients. Beaucoup 
                nous font confiance depuis de nombreuses années et n hésitent pas à nous recommander à leur entourage. Cette fidélité 
                est notre plus belle récompense et nous motive à maintenir notre niveau d exigence très élevé.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Nos Valeurs Fondamentales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-600">
                <CardContent className="pt-8 pb-8">
                  <div className="mb-4 flex justify-center">{value.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Pourquoi Nous Choisir ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
                <h3 className="text-2xl font-bold mb-4 text-blue-900">Notre Engagement Qualité</h3>
                <p className="text-gray-700 leading-relaxed">
                  Chaque intervention NHM Plomberie est réalisée selon un protocole strict : diagnostic précis, devis détaillé 
                  gratuit, travaux conformes aux normes DTU, nettoyage du chantier, garantie écrite. Nous privilégions des 
                  <strong> matériaux de qualité professionnelle</strong> issus de fournisseurs reconnus (Grohe, Geberit, Atlantic...) 
                  pour assurer la durabilité de nos installations.
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
                <h3 className="text-2xl font-bold mb-4 text-blue-900">Respect de Votre Logement</h3>
                <p className="text-gray-700 leading-relaxed">
                  Nous savons qu intervenir chez vous est une responsabilité. Nos plombiers respectent votre domicile : 
                  protection des sols et meubles, travail soigné, nettoyage systématique en fin de chantier. Nous minimisons 
                  les désagréments et travaillons efficacement pour que vous puissiez retrouver rapidement votre confort.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Nos Certifications et Garanties</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-blue-300 flex-shrink-0 mt-1" />
                <p className="text-lg">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 md:p-12 rounded-2xl border-2 border-blue-600">
              <MapPin className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-6 text-gray-900 text-center">Notre Localisation Stratégique</h2>
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Basés au coeur de <strong>Paris 17e</strong>, nous intervenons rapidement dans toute la ville 
                et les communes limitrophes des Paris et sa proche banlieue. Notre position géographique privilégiée nous permet de vous 
                garantir des <strong>temps d intervention très courts</strong>, un atout majeur pour votre tranquillité.
              </p>
              <div className="bg-white p-6 rounded-xl border border-gray-200 text-center mb-8">
                <p className="text-2xl font-bold text-blue-900 mb-2">NHM Plomberie</p>
                <p className="text-lg text-gray-700">18 rue Legendre</p>
                <p className="text-lg text-gray-700 mb-4">75017 Paris</p>
                <p className="text-lg text-blue-600 font-semibold">
                  <Link to="/zone-intervention" className="hover:underline">
                    Voir toutes nos zones d intervention →
                  </Link>
                </p>
              </div>
              <div className="text-center">
                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <a href="tel:0970705863" className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Contactez-nous : 09 70 70 58 63
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Prêt à Travailler Avec Nous ?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Découvrez l ensemble de <Link to="/services" className="text-blue-600 hover:underline font-semibold">nos services de plomberie</Link> ou 
            contactez-nous dès maintenant pour un <Link to="/contact" className="text-blue-600 hover:underline font-semibold">devis gratuit</Link>.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/services">Nos Services</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/contact">Demander un Devis</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default APropos;
